import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { OTPInput } from '@/sections/signup/OTPInput';
import { SSO } from '@/sections/signup/SSO';
import { useAuth } from '@/state/auth';

interface MFASetupModalProps {
  open: boolean;
  onClose: () => void;
  onVerifyMFA: (code: string, navigateToDashboard?: boolean) => Promise<void>;
  navigateToDashboard?: boolean;
}

export const MFASetupModal: React.FC<MFASetupModalProps> = ({
  open,
  onClose,
  onVerifyMFA,
  navigateToDashboard,
}) => {
  const { totpSecret, totpUri, isLoading } = useAuth();
  const [showManualKey, setShowManualKey] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional reset-on-open for modal state
      setTotpCode('');
      setShowManualKey(false);
    }
  }, [open]);

  // Reset when totpUri changes (new QR code generated)
  useEffect(() => {
    if (totpUri && open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional reset-on-open for modal state
      setTotpCode('');
      setShowManualKey(false);
    }
  }, [totpUri, open]);

  const handleVerifyMFA = async () => {
    if (totpCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    try {
      await onVerifyMFA(totpCode, navigateToDashboard);
      onClose();
    } catch (error) {
      console.error('MFA verification failed:', error);
    }
  };

  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(totpSecret);
      toast.success('Secret key copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy key');
    }
  };

  const isGeneratingQR = open && (!totpSecret || !totpUri);

  return (
    <Modal
      open={open}
      footer={{
        hideCancelButton: true,
      }}
      onClose={onClose}
      title="MFA Setup"
      size="xl"
    >
      <div className="text-left">
        {/* Horizontal line */}
        <div className="border-chariot-stroke mb-6 w-full border-t"></div>

        <h4 className="text-chariot-text-primary mb-2 text-lg font-semibold">
          Secure Your Account with MFA
        </h4>
        <p className="text-chariot-text-primary text-sm">
          To setup your account, we require multi-factor authentication using an
          authenticator app (such as Google, Authy, etc.)
        </p>
      </div>

      {/* Single Column Layout */}
      <div className="mt-6 flex flex-col gap-6">
        {/* Step 1: QR Code Section OR Manual Key Entry Section */}
        {!showManualKey ? (
          // QR Code Section
          <div className="border-chariot-stroke bg-chariot-background-primary rounded-md border p-6">
            <div className="flex items-start gap-6">
              {/* Left side: Text content */}
              <div className="flex flex-1 flex-col">
                <h3 className="text-chariot-text-primary mb-4 text-sm font-bold">
                  1. Scan the QR Code
                </h3>

                {!isGeneratingQR && (
                  <button
                    type="button"
                    onClick={() => setShowManualKey(true)}
                    className="text-chariot-text-secondary hover:text-chariot-text-primary text-left text-xs transition-colors"
                  >
                    <div>Can&apos;t see the code?</div>
                    <div>Click here to enter the key</div>
                  </button>
                )}
              </div>

              {/* Right side: QR Code or Loading */}
              <div className="shrink-0">
                <div className="flex justify-center">
                  {isGeneratingQR ? (
                    <div className="border-chariot-stroke bg-chariot-background-secondary flex size-32 items-center justify-center rounded-md border">
                      <Loader isLoading={true} className="size-8" />
                    </div>
                  ) : totpSecret && totpUri ? (
                    <QRCodeCanvas
                      value={totpUri}
                      size={128}
                      level="M"
                      includeMargin={true}
                      key={`${totpUri}-${totpSecret}`}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Manual Key Entry Section
          <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border p-6">
            <h3 className="text-chariot-text-primary mb-2 text-sm font-bold">
              1. Manual Key Entry
            </h3>
            <p className="text-chariot-text-primary mb-3 text-sm">
              Copy this secret key into your authenticator app.
            </p>

            {isGeneratingQR ? (
              <div className="border-chariot-stroke mb-4 flex h-12 w-full items-center justify-center rounded-md border border-dashed">
                <Loader isLoading={true} className="size-6" />
                <span className="text-chariot-text-secondary ml-2 text-sm">
                  Generating new secret key...
                </span>
              </div>
            ) : (
              <div className="border-chariot-stroke mb-4 flex w-fit items-center gap-2 rounded-full border border-dashed p-3 px-6 py-1">
                <span className="text-chariot-text-primary text-sm font-bold break-all">
                  KEY: {totpSecret}
                </span>
                <button
                  type="button"
                  onClick={handleCopyKey}
                  className="text-chariot-text-secondary hover:text-chariot-text-primary p-1 transition-colors"
                  title="Copy to clipboard"
                >
                  <ClipboardDocumentIcon className="size-4" />
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                className="w-24"
                size="sm"
                onClick={() => setShowManualKey(false)}
                disabled={isGeneratingQR}
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Code Verification Section */}
        <div className="border-chariot-stroke bg-chariot-background-primary rounded-md border p-6">
          <h3 className="text-chariot-text-primary mb-2 text-sm font-bold">
            2. Enter the 6 digit code from the app
          </h3>

          <OTPInput
            onSubmit={code => {
              setTotpCode(code);
              if (code.length === 6 && !isLoading && !isGeneratingQR) {
                handleVerifyMFA();
              }
            }}
            disabled={isGeneratingQR}
          />
        </div>

        {/* Submit Button - Full Width at Bottom */}
        <div className="mt-6">
          <Button
            onClick={handleVerifyMFA}
            disabled={isLoading || totpCode.length !== 6 || isGeneratingQR}
            className="w-full"
          >
            {isGeneratingQR ? 'Generating QR Code...' : 'Submit'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const MFASetup = () => {
  const { credentials, isLoading, verifyMFASetupSignup, setPendingMfaSignIn } =
    useAuth();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setPendingMfaSignIn(true);

    return () => {
      setPendingMfaSignIn(false);
    };
  }, [setPendingMfaSignIn]);

  return (
    <>
      <div className="text-default-light text-sm">
        <p>
          Secure your account with{' '}
          <span className="text-default font-bold">
            Multi-Factor Authentication (MFA)
          </span>
        </p>
        <p className="mt-2">
          For your business email{' '}
          <span className="text-default font-bold">{credentials.username}</span>
        </p>
        <ul className="mt-4 list-inside list-disc">
          <li>
            Download an authenticator app like Google Authenticator or Authy
          </li>
          <li>
            Use the app to scan the QR code or enter the secret key manually
          </li>
          <li>
            Enter the 6-digit code from your authenticator app to complete setup
          </li>
        </ul>
        <div className="mt-4">
          <Button
            className="text-chariot-brand-primary inline bg-transparent! p-0 text-sm"
            onClick={() => setOpen(true)}
            disabled={isLoading}
          >
            Setup MFA
          </Button>
        </div>
      </div>
      <SSO />
      <MFASetupModal
        open={open}
        onClose={() => setOpen(false)}
        onVerifyMFA={verifyMFASetupSignup}
        navigateToDashboard={true}
      />
    </>
  );
};
